/** @format */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import SortSelectDropdown from 'src/components/SortSelectDropdown';
import { ArrowDownIcon, CrossIcon, GlobeIcon } from 'src/components/icons';
import { SEARCH_SORT_OPTIONS } from 'src/constants';
import { getExtranetFilterLanguageItems } from 'src/utils/extranetLanguages';
import { useSelector } from 'src/store';
import { getDefaultSearchLanguage } from 'src/slices/search';
import { buildSearchUrlSearch, SEARCH_URL_DEFAULTS } from 'src/utils/searchUrl';

const BRAND_OPTIONS = [
  { key: 'all', labelKey: 'searchPage.allBrands' },
  { key: 'ate', label: 'ATE' },
  { key: 'vdo', label: 'VDO' }
];

const getTopicGroupKey = label => {
  const firstChar = label?.trim()?.charAt(0)?.toUpperCase() || '#';
  return /[A-Z]/.test(firstChar) ? firstChar : '#';
};

export default function SearchFilters() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedBrand, selectedTopicId, selectedLanguage, selectedSortBy, selectedKeywords, topicOptions } =
    useSelector(state => state.search);

  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [mobileFilterExpanded, setMobileFilterExpanded] = useState('brand');

  const defaultLanguage = getDefaultSearchLanguage();
  const resetAriaLabel = t('common.button.resetToDefault');

  const brandItems = useMemo(
    () =>
      BRAND_OPTIONS.map(option => ({
        key: option.key,
        label: option.labelKey ? t(option.labelKey) : option.label
      })),
    [t]
  );

  const contentItems = useMemo(() => {
    const sortedTopicOptions = [...topicOptions].sort((a, b) =>
      a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
    );
    const grouped = sortedTopicOptions.reduce((acc, topic) => {
      const groupKey = getTopicGroupKey(topic.label);
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(topic);
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b);
      })
      .map(groupKey => ({
        key: `group-${groupKey}`,
        type: 'group',
        label: groupKey,
        children: grouped[groupKey]
      }));
  }, [topicOptions]);

  const topicGroups = useMemo(
    () =>
      contentItems.map(group => ({
        key: group.key,
        label: group.label,
        children: group.children || []
      })),
    [contentItems]
  );

  const languageItems = useMemo(() => getExtranetFilterLanguageItems(t), [t]);

  const sortItems = useMemo(
    () =>
      SEARCH_SORT_OPTIONS.map(option => ({
        key: option.key,
        value: option.value,
        label: t(option.translationKey)
      })),
    [t]
  );

  const brandLabel = useMemo(() => {
    const selectedItem = brandItems.find(item => item.key === selectedBrand);
    return selectedItem?.label || t('searchPage.brand');
  }, [brandItems, selectedBrand, t]);

  const topicLabel = useMemo(() => {
    const selectedItem = topicOptions.find(item => String(item.key) === String(selectedTopicId));
    return selectedItem?.label || t('common.allTopics');
  }, [topicOptions, selectedTopicId, t]);

  const languageLabel = useMemo(() => {
    const selectedItem = languageItems.find(item => item.value === selectedLanguage);
    return selectedItem?.label || t('common.selectLanguage');
  }, [languageItems, selectedLanguage, t]);

  const sortLabel = useMemo(() => {
    const selectedItem = sortItems.find(item => item.value === selectedSortBy);
    return selectedItem?.label || t('common.sort.latest');
  }, [sortItems, selectedSortBy, t]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedBrand !== SEARCH_URL_DEFAULTS.brand) count += 1;
    if (selectedTopicId != null) count += 1;
    if (selectedLanguage !== defaultLanguage) count += 1;
    return count;
  }, [selectedBrand, selectedTopicId, selectedLanguage, defaultLanguage]);

  const replaceSearchUrl = updates => {
    const nextState = {
      brand: selectedBrand,
      topicId: selectedTopicId,
      language: selectedLanguage,
      sortBy: selectedSortBy,
      keywords: selectedKeywords,
      ...updates
    };

    navigate({
      pathname: location.pathname,
      search: buildSearchUrlSearch(nextState)
    }, { replace: true });
  };

  const handleBrandSelect = key => {
    replaceSearchUrl({ brand: key });
    setIsMobileFiltersOpen(false);
  };

  const handleTopicSelect = key => {
    replaceSearchUrl({ topicId: key });
    setIsMobileFiltersOpen(false);
  };

  const handleLanguageSelect = key => {
    replaceSearchUrl({ language: key });
    setIsMobileFiltersOpen(false);
  };

  const handleSortSelect = key => {
    const opt = sortItems.find(o => o.key === key);
    if (opt) {
      replaceSearchUrl({ sortBy: opt.value });
    }
  };

  const handleMobileAccordionChange = panel => (_, isExpanded) => {
    setMobileFilterExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      <div className="search-page__filters">
        <div className="search-page__filters-primary search-page__filters-primary--desktop">
          <CustomDropDown
            placement="bottomLeft"
            value={selectedBrand}
            defaultValue={SEARCH_URL_DEFAULTS.brand}
            onReset={handleBrandSelect}
            resetAriaLabel={resetAriaLabel}
            trigger={
              <button type="button" className="search-page__filter-trigger">
                <span className="search-page__filter-trigger-label">{brandLabel}</span>
                <ArrowDownIcon />
              </button>
            }>
            <ul className="custom-dropdown__menu" role="menu">
              {brandItems.map(item => (
                <li className="custom-dropdown__menu-item" role="none" key={item.key}>
                  <button type="button" role="menuitem" onClick={() => handleBrandSelect(item.key)}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </CustomDropDown>
          <CustomDropDown
            placement="bottomLeft"
            value={selectedTopicId}
            defaultValue={SEARCH_URL_DEFAULTS.topicId}
            onReset={() => handleTopicSelect(SEARCH_URL_DEFAULTS.topicId)}
            resetAriaLabel={resetAriaLabel}
            trigger={
              <button type="button" className="search-page__filter-trigger">
                <span className="search-page__filter-trigger-label">{topicLabel}</span>
                <ArrowDownIcon />
              </button>
            }>
            <ul className="custom-dropdown__menu" role="menu">
              <li className="custom-dropdown__menu-item custom-dropdown__menu-item--group" role="none">
                <button type="button" role="menuitem" onClick={() => handleTopicSelect(null)}>
                  {t('common.allTopics')}
                </button>
              </li>
              {contentItems.map(group => (
                <li className="custom-dropdown__menu-item custom-dropdown__menu-item--group" role="none" key={group.key}>
                  <span className="custom-dropdown__menu-group-label">{group.label}</span>
                  <ul className="custom-dropdown__menu custom-dropdown__menu--nested" role="none">
                    {(group.children || []).map(option => (
                      <li className="custom-dropdown__menu-item" role="none" key={option.key}>
                        <button type="button" role="menuitem" onClick={() => handleTopicSelect(option.key)}>
                          {option.labelKey ? t(option.labelKey) : option.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </CustomDropDown>
          <CustomDropDown
            placement="bottomLeft"
            value={selectedLanguage}
            defaultValue={defaultLanguage}
            onReset={handleLanguageSelect}
            resetAriaLabel={resetAriaLabel}
            trigger={
              <button type="button" className="search-page__filter-trigger">
                <span className="search-page__filter-trigger-label">{languageLabel}</span>
                <GlobeIcon />
              </button>
            }>
            <ul className="custom-dropdown__menu" role="menu">
              {languageItems.map(item => (
                <li key={item.key}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`topics-page__control-menu-item${
                      item.value === selectedLanguage ? ' topics-page__control-menu-item--active' : ''
                    }`}
                    onClick={() => handleLanguageSelect(item.value)}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </CustomDropDown>
        </div>
        <div className="search-page__filters-sort">
          <div className="search-page__filters-text--mobile">
            <button type="button" onClick={() => setIsMobileFiltersOpen(true)}>
              <span>{t('searchPage.filters')}</span>
              {activeFilterCount > 0 && (
                <span className="search-page__filters-count">{activeFilterCount}</span>
              )}
            </button>
          </div>
          <SortSelectDropdown
            items={sortItems}
            label={sortLabel}
            selectedKey={selectedSortBy}
            defaultValue={SEARCH_URL_DEFAULTS.sortBy}
            onReset={() => handleSortSelect(SEARCH_URL_DEFAULTS.sortBy)}
            resetAriaLabel={resetAriaLabel}
            onSelect={item => handleSortSelect(item.key)}
          />
        </div>
      </div>

      <Dialog
        open={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        fullWidth
        maxWidth="xs"
        className="search-page__filters-dialog"
        PaperProps={{ className: 'search-page__filters-dialog-paper' }}>
        <DialogTitle component="div" className="search-page__filters-dialog-title">
          <span>{t('searchPage.filterBy')}</span>
          <IconButton
            className="search-page__filters-dialog-close"
            aria-label={t('common.button.close')}
            onClick={() => setIsMobileFiltersOpen(false)}>
            <CrossIcon width={16} height={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="search-page__filters-dialog-content">
          <div className="search-page__filters-accordion-list">
            <Accordion
              expanded={mobileFilterExpanded === 'brand'}
              onChange={handleMobileAccordionChange('brand')}
              className="search-page__filters-accordion">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="search-page__filters-accordion-summary">
                {t('searchPage.brand')}
              </AccordionSummary>
              <AccordionDetails className="search-page__filters-accordion-details">
                {BRAND_OPTIONS.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={`search-page__filters-option${
                      option.key === selectedBrand ? ' search-page__filters-option--active' : ''
                    }`}
                    onClick={() => handleBrandSelect(option.key)}>
                    {option.labelKey ? t(option.labelKey) : option.label}
                  </button>
                ))}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={mobileFilterExpanded === 'topic'}
              onChange={handleMobileAccordionChange('topic')}
              className="search-page__filters-accordion">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="search-page__filters-accordion-summary">
                {t('common.allTopics')}
              </AccordionSummary>
              <AccordionDetails className="search-page__filters-accordion-details">
                <button
                  type="button"
                  className={`search-page__filters-option${
                    selectedTopicId == null ? ' search-page__filters-option--active' : ''
                  }`}
                  onClick={() => handleTopicSelect(null)}>
                  {t('common.allTopics')}
                </button>
                {topicGroups.map(group => (
                  <div className="search-page__filters-topic-group" key={group.key}>
                    <p className="search-page__filters-topic-letter">{group.label}</p>
                    {group.children.map(option => (
                      <button
                        key={option.key}
                        type="button"
                        className={`search-page__filters-option${
                          String(option.key) === String(selectedTopicId) ? ' search-page__filters-option--active' : ''
                        }`}
                        onClick={() => handleTopicSelect(option.key)}>
                        {option.labelKey ? t(option.labelKey) : option.label}
                      </button>
                    ))}
                  </div>
                ))}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={mobileFilterExpanded === 'language'}
              onChange={handleMobileAccordionChange('language')}
              className="search-page__filters-accordion">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="search-page__filters-accordion-summary">
                {t('common.selectLanguage')}
              </AccordionSummary>
              <AccordionDetails className="search-page__filters-accordion-details">
                {languageItems.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={`search-page__filters-option${
                      option.value === selectedLanguage ? ' search-page__filters-option--active' : ''
                    }`}
                    onClick={() => handleLanguageSelect(option.value)}>
                    {option.labelKey ? t(option.labelKey) : option.label}
                  </button>
                ))}
              </AccordionDetails>
            </Accordion>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
